import React from 'react';
import { Link } from 'react-router-dom';
import { Linkedin, Twitter, Github, Mail, Globe } from 'lucide-react';
import { companyInfo } from '../mock';
import { useLocale, REGIONS, LANGUAGES } from '../i18n/LocaleContext';

export default function Footer() {
  const { t, region, setRegion, language, setLanguage } = useLocale();

  const columns = [
    {
      title: t('footer.services'),
      links: [
        { label: 'Web Development', to: '/services' },
        { label: 'Data Analytics', to: '/services' },
        { label: 'Automation', to: '/services' },
      ],
    },
    {
      title: t('footer.company'),
      links: [
        { label: t('nav.home'), to: '/' },
        { label: t('nav.work'), to: '/work' },
        { label: t('nav.pricing'), to: '/pricing' },
        { label: t('nav.contact'), to: '/contact' },
      ],
    },
    {
      title: t('footer.resources'),
      links: [
        { label: t('footer.analyticsDemo'), to: '/analytics' },
        { label: t('footer.caseStudies'), to: '/work' },
        { label: t('footer.techStack'), to: '/services' },
        { label: t('footer.getQuote'), to: '/contact' },
      ],
    },
  ];

  const social = [
    { icon: Linkedin, href: companyInfo.social.linkedin, label: 'LinkedIn' },
    { icon: Twitter, href: companyInfo.social.twitter, label: 'Twitter' },
    { icon: Github, href: companyInfo.social.github, label: 'GitHub' },
    { icon: Mail, href: `mailto:${companyInfo.email}`, label: 'Email' },
  ];

  const selectClass =
    'bg-raise border border-line rounded-lg px-2.5 py-1.5 text-xs text-soft focus:outline-none focus:border-purple-500/60 cursor-pointer';

  return (
    <footer className="relative bg-surface border-t border-line overflow-hidden">
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-purple-600/5 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-pink-600/5 rounded-full blur-3xl pointer-events-none" />

      <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="py-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12">
          {/* Brand */}
          <div className="lg:col-span-2">
            <Link to="/" className="flex items-center gap-2.5 mb-6 group">
              <img
                src="/brand/logo-64.png"
                alt="SynchronicTech logo"
                className="w-10 h-10 rounded-lg object-contain transition-transform duration-300 group-hover:scale-105"
              />
              <span className="font-display text-lg font-bold text-strong">
                Synchronic<span className="text-purple-500">Tech</span>
              </span>
            </Link>
            <p className="text-soft mb-6 leading-relaxed max-w-md">{companyInfo.about}</p>
            <div className="flex gap-3">
              {social.map((s, i) => {
                const Icon = s.icon;
                return (
                  <a
                    key={i}
                    href={s.href}
                    aria-label={s.label}
                    className="w-10 h-10 rounded-lg bg-raise border border-line hover:border-purple-500/50 flex items-center justify-center transition-all duration-300 hover:bg-raise2 group"
                  >
                    <Icon className="w-5 h-5 text-soft group-hover:text-purple-500 transition-colors" />
                  </a>
                );
              })}
            </div>

            {/* Region & language */}
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <span className="inline-flex items-center gap-1.5 font-mono-ui text-[11px] tracking-wider text-faint uppercase">
                <Globe className="w-3.5 h-3.5" />
                {t('footer.region')}
              </span>
              <select
                aria-label="Region"
                value={region}
                onChange={(e) => setRegion(e.target.value)}
                className={selectClass}
              >
                {Object.entries(REGIONS).map(([key, r]) => (
                  <option key={key} value={key}>
                    {r.label} ({r.currency})
                  </option>
                ))}
              </select>
              <select
                aria-label="Language"
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                className={selectClass}
              >
                {Object.entries(LANGUAGES).map(([key, label]) => (
                  <option key={key} value={key}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {columns.map((col) => (
            <div key={col.title}>
              <h3 className="eyebrow mb-5">{col.title}</h3>
              <ul className="space-y-3">
                {col.links.map((link, i) => (
                  <li key={i}>
                    <Link
                      to={link.to}
                      className="text-soft hover:text-purple-500 transition-colors duration-300"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="py-8 border-t border-line flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="font-mono-ui text-xs text-faint">
            © {new Date().getFullYear()} {companyInfo.name}. {t('footer.rights')}
          </p>

          <a
            href={companyInfo.website}
            target="_blank"
            rel="noopener noreferrer"
            className="liquid-glass inline-flex items-center gap-2 rounded-full px-4 py-2 opacity-60 hover:opacity-100 transition-opacity duration-300"
          >
            <img src="/brand/logo-64.png" alt="" aria-hidden="true" className="w-4 h-4 object-contain" />
            <span className="font-mono-ui text-xs text-strong tracking-wide">
              {t('footer.poweredBy')}
            </span>
          </a>

          <div className="flex gap-6 text-sm">
            <span className="text-faint hover:text-purple-500 transition-colors cursor-pointer">{t('footer.privacy')}</span>
            <span className="text-faint hover:text-purple-500 transition-colors cursor-pointer">{t('footer.terms')}</span>
            <span className="text-faint hover:text-purple-500 transition-colors cursor-pointer">{t('footer.cookies')}</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
